import { useCallback, useEffect, useState } from "react";
import { TestFilterNote, TestRowBadge } from "./TestFilterNote";
import { BOT_TRANSFER_REASONS } from "../../shared/bot-transfer-reasons";
import "./admin-bot-transfer.css";

// F102 master switch (Servis): the client's beta step 2 submit queues the
// BotFather transfer for the Mac Mini runner. While the switch is OFF (the
// shipped default) jobs still queue, the machine API just hands nothing out -
// the same rehearsal contract the Stripe import uses. The panel shows the
// queue so turning the switch on is a decision made with the numbers on
// screen.

// The server's reason vocabulary itself (F113), never a hand-typed mirror; an
// unknown value renders raw rather than throwing the whole panel away on a
// missing locale key.
const KNOWN_REASONS: readonly string[] = BOT_TRANSFER_REASONS;

interface BotTransferJobRow {
  id: string;
  client_id: string;
  bot_username: string;
  recipient_username: string;
  status: "pending" | "running" | "done" | "failed";
  attempts: number;
  reason: string | null;
  created_at: string;
  client_name: string | null;
  is_test: boolean;
}

interface BotTransferOverview {
  settings: { enabled: boolean };
  counts: { pending: number; running: number; done: number; failed: number };
  waiting: number;
  jobs: BotTransferJobRow[];
}

export function AdminBotTransfer(props: {
  t: (key: string) => string;
  csrfToken: string;
  hideTestTenants: boolean;
  onHideTestTenantsChange: (hide: boolean) => void;
  onOpenClient?: (clientId: string) => void;
}) {
  const [overview, setOverview] = useState<BotTransferOverview | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveState, setSaveState] = useState<"idle" | "done" | "error">("idle");

  const load = useCallback(async () => {
    try {
      const response = await fetch("/api/admin/bot-transfer", { credentials: "include" });
      if (!response.ok) {
        setLoadFailed(true);
        return;
      }
      setOverview(await response.json() as BotTransferOverview & { ok: true });
      setLoadFailed(false);
    } catch {
      setLoadFailed(true);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function saveEnabled(enabled: boolean) {
    setSaving(true);
    setSaveState("idle");
    try {
      const response = await fetch("/api/admin/bot-transfer/settings", {
        method: "PUT",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "X-Launchpad-CSRF": props.csrfToken,
          "Idempotency-Key": crypto.randomUUID()
        },
        body: JSON.stringify({ enabled })
      });
      if (!response.ok) {
        setSaveState("error");
        return;
      }
      setSaveState("done");
      await load();
    } catch {
      setSaveState("error");
    } finally {
      setSaving(false);
    }
  }

  const jobs = overview?.jobs ?? [];
  const visibleJobs = props.hideTestTenants ? jobs.filter((job) => !job.is_test) : jobs;
  const hiddenJobs = jobs.length - visibleJobs.length;

  return (
    <div className="bot-transfer-admin">
      {loadFailed && <p className="message error-message" role="alert">{props.t("staffConsole.botTransfer.loadFailed")}</p>}
      {overview && (
        <>
          <label className="checkbox-row bot-transfer-switch">
            <input
              type="checkbox"
              checked={overview.settings.enabled}
              disabled={saving}
              onChange={(event) => void saveEnabled(event.target.checked)}
            />
            <span>{props.t("staffConsole.botTransfer.enable")}</span>
          </label>
          <p className="admin-tool-hint">{props.t("staffConsole.botTransfer.switchHint")}</p>
          {saveState === "done" && <p className="message success-message" role="status">{props.t("staffConsole.botTransfer.saved")}</p>}
          {saveState === "error" && <p className="message error-message" role="alert">{props.t("staffConsole.botTransfer.saveFailed")}</p>}
          <p className="bot-transfer-waiting">
            {props.t("staffConsole.botTransfer.waiting")} <strong>{overview.waiting}</strong>
          </p>
          <TestFilterNote
            shown={visibleJobs.length}
            hidden={hiddenJobs}
            t={props.t}
            onShowTest={() => props.onHideTestTenantsChange(false)}
          />
          {visibleJobs.length === 0
            ? <p className="admin-tool-hint">{props.t("staffConsole.botTransfer.empty")}</p>
            : (
              <ul className="bot-transfer-job-list">
                {visibleJobs.map((job) => (
                  <li key={job.id} className="bot-transfer-job" data-job-status={job.status}>
                    <div className="bot-transfer-job-main">
                      <strong className="bot-transfer-job-client">{job.client_name ?? job.client_id}</strong>
                      {job.is_test && <TestRowBadge t={props.t} />}
                      <span className="bot-transfer-job-names">@{job.bot_username} → @{job.recipient_username}</span>
                    </div>
                    <div className="bot-transfer-job-meta">
                      <span className={`status-pill bot-transfer-pill-${job.status}`}>
                        {props.t(`staffConsole.botTransfer.status.${job.status}`)}
                      </span>
                      {job.status === "failed" && job.reason && (
                        <span className="bot-transfer-job-reason">
                          {KNOWN_REASONS.includes(job.reason) ? props.t(`mail.botTransferReason.${job.reason}`) : job.reason}
                        </span>
                      )}
                      {props.onOpenClient && (
                        <button
                          className="text-button"
                          type="button"
                          onClick={() => props.onOpenClient?.(job.client_id)}
                        >{props.t("staffConsole.botTransfer.openClient")}</button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
        </>
      )}
    </div>
  );
}
